// SPDX-License-Identifier: GPL-3.0-or-later
// The look of each boss. One SigmaBot spec and one scale for each boss name. The table is fixed, so
// the same name always gives the same boss. An unknown name gets a look from a hash of the name.
// There is no random source here.

import {
  SIGMABOT_ACCESSORIES,
  SIGMABOT_BODIES,
  SIGMABOT_EYES,
  SIGMABOT_HATS,
  SIGMABOT_MOUTHS,
  SIGMABOT_PALETTES,
  type SigmaBotSpec,
} from "../../table/characters/sigmabot/index.ts";

/** The look of one boss: the spec, and the scale that makes the bot a large boss. */
export interface BossLook {
  readonly spec: SigmaBotSpec;
  readonly scale: number;
}

/** The scale of a boss with an unknown name. */
export const DEFAULT_BOSS_SCALE = 1.7;

/** One look for each boss of the run. The keys are the names that the run director sends. */
export const BOSS_LOOKS: Readonly<Record<string, BossLook>> = {
  "Ember Bean": {
    spec: {
      body: "bean",
      palette: "ember",
      eyes: "angry",
      mouth: "fang",
      hat: "antlers",
      accessory: "none",
    },
    scale: 1.5,
  },
  "Glitch Pip": {
    spec: {
      body: "tiny",
      palette: "mint",
      eyes: "dizzy",
      mouth: "wavy",
      hat: "none",
      accessory: "antenna",
    },
    scale: 2.6,
  },
  "Static Nub": {
    spec: {
      body: "round",
      palette: "slate",
      eyes: "spark",
      mouth: "flat",
      hat: "beanie",
      accessory: "headphones",
    },
    scale: 1.7,
  },
  "Rusty Cog": {
    spec: {
      body: "robot",
      palette: "cocoa",
      eyes: "smug",
      mouth: "teeth",
      hat: "helmet",
      accessory: "badge",
    },
    scale: 1.9,
  },
  "Grand Widget": {
    spec: {
      body: "tall",
      palette: "gold",
      eyes: "wide",
      mouth: "grin",
      hat: "crown",
      accessory: "cape",
    },
    scale: 1.6,
  },
};

/** A 32-bit FNV-1a hash of a text. */
function hashOf(text: string): number {
  let h = 0x811c9dc5;
  for (const ch of text) {
    h = Math.imul(h ^ (ch.codePointAt(0) ?? 0), 0x01000193) >>> 0;
  }
  return h;
}

/** One entry of a list. Each salt gives another entry for the same hash. */
function pickBy<T>(list: readonly T[], hash: number, salt: number): T {
  const mixed = Math.imul(hash ^ Math.imul(salt, 0x9e3779b1), 0x85ebca6b) >>> 0;
  return list[(mixed >>> 7) % list.length] as T;
}

/** The look of a boss name. A name that the table does not know gets a look from its hash. */
export function bossLook(name: string): BossLook {
  const known = Object.hasOwn(BOSS_LOOKS, name) ? BOSS_LOOKS[name] : undefined;
  if (known !== undefined) return known;
  const h = hashOf(name);
  return {
    spec: {
      body: pickBy(SIGMABOT_BODIES, h, 1),
      palette: pickBy(SIGMABOT_PALETTES, h, 2),
      eyes: pickBy(SIGMABOT_EYES, h, 3),
      mouth: pickBy(SIGMABOT_MOUTHS, h, 4),
      hat: pickBy(SIGMABOT_HATS, h, 5),
      accessory: pickBy(SIGMABOT_ACCESSORIES, h, 6),
    },
    scale: DEFAULT_BOSS_SCALE,
  };
}
